import { CheckIcon, CopyIcon, RepeatIcon, ViewIcon, ViewOffIcon, WarningIcon } from '@chakra-ui/icons'
import {
    Box,
    Button,
    FormControl,
    FormLabel,
    HStack,
    IconButton,
    Input,
    InputGroup,
    InputRightElement,
    List,
    ListIcon,
    ListItem,
    Slider,
    SliderFilledTrack,
    SliderMark,
    SliderThumb,
    SliderTrack,
    Text,
    useClipboard,
    useToast,
    VStack,
} from '@chakra-ui/react'
import React, { useEffect, useRef } from 'react'
import { PasswordRuleNames, type PasswordRules } from '../dtos/Account/PasswordRules'
import { usePasswordGenerator } from '../hooks/usePasswordGenerator'

export interface PasswordGeneratorProps {
    rules: PasswordRules;
    onPasswordGenerated?: (password: string) => void;
    showValidation?: boolean;
    autoGenerate?: boolean;
}

export const PasswordGenerator: React.FC<PasswordGeneratorProps> = ({
    rules,
    onPasswordGenerated,
    showValidation = true,
    autoGenerate = false,
}) => {
    const [showPassword, setShowPassword] = React.useState(false)
    const [passwordLength, setPasswordLength] = React.useState(Math.max(rules.minLenght || 8, 12))

    const { password, generatePassword, isValid } = usePasswordGenerator({
        rules,
        length: passwordLength,
    })
    const prevPassword = useRef(password)

    const { onCopy } = useClipboard(password)
    const toast = useToast()

    useEffect(() => {
        if (autoGenerate) {
            generatePassword()
        }
    }, [autoGenerate, generatePassword])

    useEffect(() => {
        if (password && onPasswordGenerated && password !== prevPassword.current) {
            onPasswordGenerated(password)
            prevPassword.current = password
        }
    }, [password, onPasswordGenerated])

    const handleCopy = () => {
        onCopy()
        toast({
            title: 'Heslo zkopírováno',
            description: 'Heslo bylo zkopírováno do schránky',
            status: 'success',
            duration: 2000,
            isClosable: true,
        })
    }

    const getRuleStatus = (ruleKey: keyof PasswordRules) => {
        if (!password) return null

        const ruleValue = rules[ruleKey]
        if (ruleValue === undefined || ruleValue === false || ruleValue === 0) {
            return null
        }

        const ruleValidator = PasswordRuleNames.find(([key]) => key === ruleKey)
        if (!ruleValidator) return null

        if (ruleKey === 'minLenght') {
            return password.length >= (ruleValue as number)
        }
        if (ruleKey === 'requireDigit') {
            return /[0-9]/.test(password)
        }
        if (ruleKey === 'requireLowercase') {
            return /[a-z]/.test(password)
        }
        if (ruleKey === 'requireUppercase') {
            return /[A-Z]/.test(password)
        }
        if (ruleKey === 'requireNonLetterOrDigit') {
            return /[^\w]/.test(password)
        }

        return false
    }

    return (
        <VStack spacing={4} align="stretch">
            <FormControl variant="fullWidth">
                <FormLabel>Generované heslo</FormLabel>
                <InputGroup size="md">
                    <Input
                        pr="4.5rem"
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        readOnly
                        placeholder="Klikněte na 'Generovat heslo'"
                        onClick={password ? handleCopy : undefined}
                        cursor={password ? 'pointer' : 'default'}
                    />
                    <InputRightElement width="4.5rem"
                        onClick={password ? handleCopy : undefined}
                        cursor={password ? 'pointer' : 'default'}>
                        <HStack spacing={1}>
                            <IconButton
                                aria-label={showPassword ? 'Skrýt heslo' : 'Zobrazit heslo'}
                                icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                                size="sm"
                                variant="ghost"
                                onClick={(e) => {
                                    setShowPassword(!showPassword)
                                    e.stopPropagation()
                                }}
                                padding={1.5}
                            />
                            {password && (
                                <IconButton
                                    aria-label="Kopírovat heslo"
                                    icon={<CopyIcon />}
                                    size="sm"
                                    variant="ghost"
                                    onClick={(e) => {
                                        handleCopy()
                                        e.stopPropagation()
                                    }}
                                    padding={1.5}
                                />
                            )}
                        </HStack>
                    </InputRightElement>
                </InputGroup>
            </FormControl>

            <FormControl pb={4}>
                <FormLabel>
                    Délka hesla: {passwordLength}
                </FormLabel>
                <Slider
                    min={rules.minLenght || 8}
                    max={32}
                    step={1}
                    value={passwordLength}
                    onChange={setPasswordLength}
                >
                    <SliderMark value={rules.minLenght || 8} mt={2} ml={1} fontSize="sm">
                        {rules.minLenght || 8}
                    </SliderMark>
                    <SliderMark value={32} mt={2} ml={-5} fontSize="sm">
                        32
                    </SliderMark>
                    <SliderTrack>
                        <SliderFilledTrack />
                    </SliderTrack>
                    <SliderThumb />
                </Slider>
            </FormControl>

            <Button
                leftIcon={<RepeatIcon />}
                onClick={generatePassword}
                width="full"
            >
                Generovat heslo
            </Button>

            {showValidation && password && (
                <Box>
                    <Text fontWeight="bold" mb={2} textAlign='left'>
                        Požadavky na heslo:
                    </Text>
                    <List spacing={1}>
                        {PasswordRuleNames.map(([key, nameFactory]) => {
                            const ruleValue = rules[key]
                            if (ruleValue === undefined || ruleValue === false || ruleValue === 0) {
                                return null
                            }

                            const isRuleMet = getRuleStatus(key)
                            const ruleName = nameFactory(ruleValue)

                            return (
                                <ListItem key={key} display="flex" alignItems="center" textAlign='left'>
                                    <ListIcon
                                        as={isRuleMet ? CheckIcon : WarningIcon}
                                        color={isRuleMet ? 'green.500' : 'orange.500'}
                                        fontSize="sm"
                                    />
                                    <Text
                                        color={isRuleMet ? 'green.600' : 'orange.600'}
                                        textDecoration={isRuleMet ? 'none' : 'none'}
                                        fontSize="sm"
                                    >
                                        {ruleName}
                                    </Text>
                                </ListItem>
                            )
                        })}
                    </List>
                    {isValid && (
                        <HStack mt={4} justifyContent="center">
                            <CheckIcon color="green.500" />
                            <Text color="green.500" fontWeight="bold" textAlign="center">
                                Heslo splňuje všechny požadavky
                            </Text>
                        </HStack>
                    )}
                </Box>
            )}
        </VStack>
    )
}